import ListFilters from "@/common/components/ListFilters";
import Table from "@/common/components/Table";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { useProducts } from "@/common/hooks/queries/useProducts";
import { useGeoOptionsByType } from "@/common/hooks/queries/useGeoOptionsByType";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { GeoTransformers } from "@/common/services/geoTransformers";
import { ProductData } from "@/common/types/product.type";
import { useAnpHistoricalPrices } from "../../hooks/useAnpHistoricalPrices";
import { AnpPricesTransformer } from "../../services/anpPricesTransformer";
import { anpPricesByGeoTypeColumns } from "../../constants/anpPricesDefaultColumns.constant";
import { defaultMeta } from "@/common/constants/defaultMeta.constant";

interface AnpHistoricalPricesTableProps {
  reportId?: string;
}

const AnpHistoricalPricesTable = ({ reportId }: AnpHistoricalPricesTableProps) => {
  const { filterParams, setFilterParams } = useFilterParams();

  const [searchCity, setSearchCity] = useDebounce('');
  const [searchProduct, setSearchProduct] = useDebounce('');

  const { data: productOptions = [] } = useProducts({
    filters: { search: searchProduct },
    mapper: Mapper.mapToOptions<ProductData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  const { data: cityOptions = [] } = useGeoOptionsByType({
    filters: {
      type: 'cities',
      search: searchCity,
    },
    mapper: Mapper.mapWithTransformer({ transformer: GeoTransformers.optionsTransformer() }),
  });

  const {
    data: anpPrices = [],
    meta = defaultMeta,
    isFetching: anpPricesIsFetching,
    error: anpPricesError = {} as any,
    refetch: refetchAnpPrices,
  } = useAnpHistoricalPrices({
    filters: {
      reportId,
      productId: filterParams.product?.value,
      geoCityId: filterParams.cities?.value,
      page: Number(filterParams.page) || 1,
    },
    mapper: Mapper.mapWithTransformer({ transformer: AnpPricesTransformer.tableTransformer('Geo::City') }),
  });

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-340px)]"
      statusCode={anpPricesError?.status}
      refetchFunction={refetchAnpPrices}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <ListFilters
            filters={[
              {
                className: 'min-w-40',
                placeholder: 'Produto',
                options: [{ label: 'Todos', value: '' }, ...productOptions],
                onChange: (product) => setFilterParams((prev: any) => ({ ...prev, product })),
                onInputChange: setSearchProduct,
                value: filterParams.product,
              },
                {
                  className: 'min-w-48',
                  placeholder: 'Cidade',
                  options: [{ label: 'Todos', value: '' }, ...cityOptions],
                onChange: (option) => setFilterParams((prev: any) => ({ ...prev, cities: option })),
                onInputChange: setSearchCity,
                value: filterParams.cities,
              },
            ]}
          />
        </div>

        <div>
          <Table
            className="max-h-[calc(100vh-276px-7.5rem)]"
            columns={anpPricesByGeoTypeColumns['Geo::City']}
            rows={anpPrices}
            isLoadingData={anpPricesIsFetching}
            hasPagination
            rowEmptyValue="--"
            totalCount={meta.total}
            totalPages={meta.total_pages}
            page={Number(filterParams.page) || 1}
            setPage={(page) => setFilterParams((prev: any) => ({ ...prev, page }))}
          />
        </div>
      </div>
    </ErrorMessageWrapper>
  );
};

export default AnpHistoricalPricesTable;
